/**
 * ── TABLE → CARDS: WHICH ROWS BECOME CARDS, AND HOW MANY (2026-09-10) ─────────────
 *
 * Jack, on Now Selling / Just Launched at division scope: "the featured home only
 * displays one home … need to set up where it shows one featured home per
 * community/collection and one per market if there's multiple markets."
 *
 * MEASURED ON PROD (ATL, Division focus, the template's own table, 5 communities ×
 * 2 rows across Chattanooga / Atlanta / Middle Georgia): Plan cards gave ONE card —
 * 4259 Tillie Circle, row 1 — and no siblings. The Inspector's mint matched every
 * row to a home by its ADVERTISED number ("39", "1", "66", "29" …) across EVERY pack
 * in hand and took the first pack that had a home ending in that number. Asbury Oak,
 * the first pack, has lots that shorten to most of those numbers, so rows 3–10 all
 * "matched" Asbury Oak, the one-per-community rule saw Asbury Oak already used, and
 * skipped them. One card, nine silent skips.
 *
 * The table already knows exactly which home each row is: `__lots` records the FULL
 * lot id per row (echSendTime reads it for the send-time price), and a full lot id is
 * unique. So a row is resolved by its full lot first — exact match, any pack — and the
 * short-number scan is only the fallback for a table that carries no lots (a CSV
 * fill, a typed row).
 *
 * HOW MANY CARDS, the rule Jack stated, in this order:
 *   rows span several MARKETS      → one card per market (its leading row)
 *   rows span several COMMUNITIES  → one card per community
 *   one community                  → its first three homes
 * Always capped at the section's five. A row's market is read off its lennar.com url
 * (/new-homes/<state>/<market>/…) — the table stores that url per row, so the answer
 * does not depend on which feed built the pack. A row with no readable market falls
 * into its community's own group, so an unknown never collapses two markets into one.
 */
import { homeKey, type Pack } from "./dynamicFill";
import type { HomesiteInfo } from "../types/api";
import { echShortLot, ECH_HOME_PICK, ECH_PLAN_PICK } from "./echDyn";
import { homePickList, planPickKey } from "./echDynPick";

export interface CardMint { key: string; val: string }

/** the section's capacity — five cards (Jack's 2026-08-21 rule) */
export const CARD_CAP = 5;

/** `/new-homes/<state>/<market>/…` → "state/market" ("" when the url has no market segment) */
export function echMarketSlugOfUrl(u: unknown): string {
  const m = /\/new-homes\/([a-z0-9-]+)\/([a-z0-9-]+)(\/|$)/i.exec(String(u || ""));
  return m ? (m[1] + "/" + m[2]).toLowerCase() : "";
}

interface RowHit { P: Pack; hit: HomesiteInfo }

/** the home a table row is showing: full lot first (exact, unique), advertised number second */
function rowHome(d: Record<string, unknown>, lots: Record<string, string>, ps: Pack[], n: number): RowHit | null {
  const lot = String(lots[String(n)] || "").trim();
  if (lot) {
    for (const P of ps) {
      const hit = (P.C.homesites || []).find((h) => String((h as { lot?: unknown }).lot || "").trim() === lot);
      if (hit) return { P, hit };
    }
  }
  const want = String(d["home" + n + "Homesite"] || "").trim();
  if (!want) return null;
  for (const P of ps) {
    const hit = (P.C.homesites || []).find((h) => echShortLot((h as { lot?: string }).lot, P.code) === want);
    if (hit) return { P, hit };
  }
  return null;
}

/** the plan a table row names, at floorplans level */
function rowPlan(d: Record<string, unknown>, ps: Pack[], n: number): { P: Pack; idx: number } | null {
  const want = String(d["home" + n + "Plan"] || "").trim().toLowerCase();
  if (!want) return null;
  for (const P of ps) {
    const idx = (P.C.plans || []).findIndex((pl) => String(pl.name || "").trim().toLowerCase() === want);
    if (idx > -1) return { P, idx };
  }
  return null;
}

/**
 * The cards a listing table becomes, in row order. `d` is the table's data, `ps` the
 * packs in hand, `lvl` what the table's rows are (homes / plans). Row 1's mint goes
 * on the swapped block; the rest become siblings.
 */
export function echMintCards(d: Record<string, unknown>, ps: Pack[], lvl: string, cap = CARD_CAP): CardMint[] {
  if (!ps.length) return [];
  let lots: Record<string, string> = {};
  try { lots = JSON.parse(String(d.__lots || "{}")) || {}; } catch { lots = {}; }

  interface Row { n: number; mint: CardMint; comm: string; mkt: string }
  const rows: Row[] = [];
  for (let n = 1; n <= 10; n++) {
    if (lvl === "plans") {
      const r = rowPlan(d, ps, n);
      if (!r) continue;
      const v = planPickKey(ps, r.P.code + "|p|" + r.idx);
      if (!v) continue;
      rows.push({ n, mint: { key: ECH_PLAN_PICK, val: v }, comm: r.P.code, mkt: echMarketSlugOfUrl(d["home" + n + "Url"]) || echMarketSlugOfUrl(r.P.C.plans?.[r.idx]?.url) });
    } else if (lvl === "homes") {
      const r = rowHome(d, lots, ps, n);
      if (!r) continue;
      const v = homePickList(ps, [homeKey(r.P.code, r.hit)]);
      if (!v) continue;
      const h = r.hit as { url?: string; market?: string };
      rows.push({ n, mint: { key: ECH_HOME_PICK, val: v }, comm: r.P.code, mkt: echMarketSlugOfUrl(d["home" + n + "Url"]) || echMarketSlugOfUrl(h.url) || String(h.market || "").toLowerCase() });
    }
  }
  if (!rows.length) return [];

  const mkts = Array.from(new Set(rows.map((r) => r.mkt).filter(Boolean)));
  const comms = Array.from(new Set(rows.map((r) => r.comm)));
  /* several markets → group by market (an unreadable market keeps its community as
     its own group); otherwise by community */
  const groupOf = mkts.length > 1
    ? (r: Row) => (r.mkt ? "m:" + r.mkt : "c:" + r.comm)
    : (r: Row) => "c:" + r.comm;
  const groups = new Set(rows.map(groupOf)).size;
  const perGroup = groups <= 1 && comms.length <= 1 ? 3 : 1;

  const out: CardMint[] = [];
  const used: Record<string, number> = {};
  for (const r of rows) {
    if (out.length >= cap) break;
    if (out.some((x) => x.val === r.mint.val)) continue;
    const g = groupOf(r);
    if ((used[g] || 0) >= perGroup) continue;
    used[g] = (used[g] || 0) + 1;
    out.push(r.mint);
  }
  return out;
}
